import React from 'react';
import { Card, Typography, Spin, Alert, Input, Space, message, Tooltip, Modal, Form, Select } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import { useGetToPlnCostCenters } from '../../../hooks/genericHooks';
import type { ToPlnCostCenters } from '../../../types';
import CsvExcelImport from '../../../components/common/CsvExcelImport';
import { updateData } from '../../../services/api';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const ToPlnProjCostCentersList: React.FC = () => {
  const { data, isLoading, error, refetch } = useGetToPlnCostCenters();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState<string | undefined>(undefined);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [currentRecord, setCurrentRecord] = React.useState<ToPlnCostCenters | null>(null);
  const [form] = Form.useForm<Pick<ToPlnCostCenters, 'resourceidname'|'resourceid'|'category'|'unit'>>();

  const source: ToPlnCostCenters[] = React.useMemo(() => data?.data ?? [], [data]);

  // Text tokens for AND-based multi-word matching (order-agnostic)
  const textTokens = React.useMemo(() => {
    return search
      .toLowerCase()
      .split(/\s+/)
      .map(t => t.trim())
      .filter(Boolean);
  }, [search]);

  // First filter by text, then compute category options from that result (cascading), finally apply category filter
  const textFiltered = React.useMemo(() => {
    if (textTokens.length === 0) return source;
    return source.filter((item) => {
      const haystack = [
        String(item.id ?? ''),
        item.resourceidname ?? '',
        item.resourceid ?? '',
        item.category ?? '',
        item.unit ?? '',
      ]
        .join(' \u0000 ')
        .toLowerCase();
      return textTokens.every(tok => haystack.includes(tok));
    });
  }, [source, textTokens]);

  const categoryOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of textFiltered) {
      const c = (r.category || '').trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [textFiltered]);

  const filteredData = React.useMemo(() => {
    const base = textFiltered;
    if (!categoryFilter) return base;
    return base.filter(r => (r.category || '').trim() === categoryFilter);
  }, [textFiltered, categoryFilter]);

  // Columns based on ToPlnCostCenters interface
  const columns: any[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      sorter: (a: ToPlnCostCenters, b: ToPlnCostCenters) => (a.id ?? 0) - (b.id ?? 0),
      exportValue: (r: ToPlnCostCenters) => r.id,
    },
    {
      title: 'Resource Name',
      dataIndex: 'resourceidname',
      key: 'resourceidname',
      width: 220,
      sorter: (a: ToPlnCostCenters, b: ToPlnCostCenters) =>
        (a.resourceidname || '').localeCompare(b.resourceidname || ''),
      exportValue: (r: ToPlnCostCenters) => r.resourceidname,
    },
    {
      title: 'Resource ID',
      dataIndex: 'resourceid',
      key: 'resourceid',
      width: 160,
      sorter: (a: ToPlnCostCenters, b: ToPlnCostCenters) =>
        (a.resourceid || '').localeCompare(b.resourceid || ''),
      exportValue: (r: ToPlnCostCenters) => r.resourceid,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      sorter: (a: ToPlnCostCenters, b: ToPlnCostCenters) =>
        (a.category || '').localeCompare(b.category || ''),
      exportValue: (r: ToPlnCostCenters) => r.category,
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
      sorter: (a: ToPlnCostCenters, b: ToPlnCostCenters) =>
        (a.unit || '').localeCompare(b.unit || ''),
      exportValue: (r: ToPlnCostCenters) => r.unit,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 90,
      render: (_: any, record: ToPlnCostCenters) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              action="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                if (record?.id == null) {
                  message.warning('Missing ID for this row');
                  return;
                }
                setCurrentRecord(record);
                form.setFieldsValue({
                  resourceidname: record.resourceidname,
                  resourceid: record.resourceid,
                  category: record.category,
                  unit: record.unit,
                });
                setIsEditOpen(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ margin: 16, padding: 0 }} styles={{ body: { padding: 0 } }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px 8px 24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Project Cost Centers
        </Title>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '8px 24px 8px 24px',
          borderBottom: '1px solid #f0f0f0',
          gap: 8,
        }}
      >
        <Input
          type="text"
          placeholder="Search by any field"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 220, maxWidth: 320 }}
          allowClear
        />
        <Select
          allowClear
          showSearch
          placeholder="Category"
          optionFilterProp="label"
          value={categoryFilter}
          onChange={(v) => setCategoryFilter(v)}
          options={categoryOptions}
          style={{ minWidth: 160 }}
        />
      </div>

      <div style={{ padding: '8px 24px 16px 24px' }}>
        {error && (
          <Alert
            type="error"
            message="Failed to load cost centers"
            description={error.message}
            showIcon
            style={{ marginBottom: 8 }}
          />
        )}
        {isLoading ? (
          <Spin />
        ) : (
          <StandardTable
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            size="small"
            enableExport={true}
            footerLeftExtra={
              <CsvExcelImport
                tableName="to_pln_costcenters"
                onSuccess={() => {
                  message.success('Import completed. Refreshing list...');
                  refetch?.();
                }}
                onError={() => message.error('Import failed')}
              />
            }
          />
        )}
      </div>

      <Modal
        open={isEditOpen}
        title={
          <span>
            Edit Cost Center {currentRecord?.id ? `#${currentRecord.id}` : ''}
          </span>
        }
        onCancel={() => !saving && setIsEditOpen(false)}
        onOk={async () => {
          if (!currentRecord?.id) { message.error('Invalid record'); return; }
          try {
            const values = await form.validateFields();
            setSaving(true);
            await updateData('to_pln_costcenters', currentRecord.id, values, 'id');
            message.success('Updated successfully');
            setIsEditOpen(false);
            setCurrentRecord(null);
            form.resetFields();
            await refetch?.();
          } catch (e:any) {
            if (e?.errorFields) return; // validation error
            message.error(e?.message || 'Update failed');
          } finally {
            setSaving(false);
          }
        }}
        okButtonProps={{ loading: saving }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ resourceidname: '', resourceid: '', category: '', unit: '' }}
        >
          <Form.Item label="ID">
            <Input value={currentRecord?.id} disabled />
          </Form.Item>
          <Form.Item name="resourceidname" label="Resource Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Electrical" />
          </Form.Item>
          <Form.Item name="resourceid" label="Resource ID" rules={[{ required: true }]}>
            <Input placeholder="e.g. E-1001" />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Input placeholder="e.g. Labor/Material/Service" />
          </Form.Item>
          <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
            <Input placeholder="e.g. pcs, m, h" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ToPlnProjCostCentersList;
